import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { createPortal } from 'react-dom'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { store } from '../../../store/store'
import { selectAccountId } from '../../../store/auth/authSlice'
import { AIAgentType } from '../../../models/AIAgent'
import { addMessage, clearChat, selectMessagesForStreaming, updateStreamingMessage } from '../../../store/streamingChat/streamingChat'
import { useStreamingAiAgent } from '../../AIAgent/hooks/useStreamingAiAgent'
import { useCreateTemplateMutation, useUpdateTemplateMutation } from '../../../store/templateLibrary/templateLibrary'
import { CustomTemplateValue } from '../../../models/TemplateLibrary'
import { Button } from '../../../components/Button/Button'
import { AddSampleModal } from './AddSampleModal'
import { TemplateChatInputBar } from './TemplateChatInputBar'
import { HeaderContext } from '../../LayoutWithLeftSidebar/HeaderContext'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { getTemplateLibraryTrackingDetails, TemplateCreationMethod, TemplateGeneratorSource } from './tracking'

interface TemplateGeneratorResult {
  message: string
  template?: string
}

export interface GenerateTemplatePageState {
  templateTypeName: string
  templateType: string
  templateTypeId: string
  templateName?: string
  templateDescription?: string
  templateScope?: 'account' | 'user'
}

export const GenerateTemplatePage: React.FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { templateTypeName, templateType, templateTypeId, templateName, templateDescription, templateScope } =
    (location.state as GenerateTemplatePageState) ?? {}
  const accountId = useAppSelector(selectAccountId)
  const { portalRef } = useContext(HeaderContext)
  const { tracked } = useTracking()

  const hasRequiredState = templateType && templateTypeId && templateTypeName

  const [isLoading, setIsLoading] = useState(false)
  const [chatMessage, setChatMessage] = useState('')
  const [showAddSampleModal, setShowAddSampleModal] = useState(false)
  const [addedSample, setAddedSample] = useState('')

  const latestTemplateRef = useRef<string | null>(null)
  const isMountedRef = useRef(true)

  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const [createTemplate] = useCreateTemplateMutation()
  const [updateTemplate] = useUpdateTemplateMutation()

  const latestAiAgentIdRef = useRef<string | null>(null)

  const onTextDelta = useCallback(
    (data: TemplateGeneratorResult) => {
      const { message, template } = data
      if (message) {
        dispatch(updateStreamingMessage(message))
      }
      if (template) {
        latestTemplateRef.current = template
      }
    },
    [dispatch],
  )

  const onNewAiAgentId = useCallback((newAiAgentId: string) => {
    latestAiAgentIdRef.current = newAiAgentId
  }, [])

  const { triggerExecuteAiAgentStreaming } = useStreamingAiAgent<TemplateGeneratorResult>({ onTextDelta, onNewAiAgentId })

  useEffect(() => {
    if (!hasRequiredState) {
      navigate('/workspace/setup/templates', { replace: true })
    }
  }, [hasRequiredState, navigate])

  useEffect(() => {
    return () => {
      dispatch(clearChat())
    }
  }, [dispatch])

  const _handleCreateGeneratedTemplate = async (result: CustomTemplateValue, message: string, sample: string) => {
    await executeTemplateGenerator(result, message, sample)

    const templateContent = latestTemplateRef.current
    latestTemplateRef.current = null

    if (templateContent) {
      await updateTemplate({
        accountId,
        templateId: result.id,
        request: {
          template: { template: templateContent },
        },
      }).unwrap()
    }

    if (!isMountedRef.current) return

    const initialMessages = selectMessagesForStreaming(store.getState())

    navigate(`/workspace/setup/templates/template/${result.id}`, {
      replace: true,
      state: {
        aiAgentId: latestAiAgentIdRef.current,
        initialMessages: initialMessages.map(({ type, message }) => ({ type, message })),
      },
    })
  }

  const _executeTemplateGenerator = async (result: CustomTemplateValue, message: string, sample: string) => {
    await triggerExecuteAiAgentStreaming({
      accountId,
      payload: {
        type: AIAgentType.TEMPLATE_GENERATOR,
        context: {
          templateId: result.id,
          template: '',
          message,
          ...(sample ? { example: sample } : {}),
        },
      },
    })
  }

  const executeTemplateGenerator = tracked(
    _executeTemplateGenerator,
    { eventName: TrackEvent.TEMPLATE_GENERATOR_EXECUTED },
    {
      eventDetailsExtractor: (result, _message, sample) => ({
        templateId: result.id,
        type: result.type,
        sampleAttached: !!sample,
        source: TemplateGeneratorSource.GenerateTemplate,
      }),
    },
  )

  const handleGeneratedTemplateCreated = tracked(
    _handleCreateGeneratedTemplate,
    { eventName: TrackEvent.NEW_TEMPLATE_CREATED },
    {
      eventDetailsExtractor: (result) => ({ ...getTemplateLibraryTrackingDetails(result), method: TemplateCreationMethod.AiGenerated }),
    },
  )

  const _handleTemplateCreated = (result: CustomTemplateValue) => {
    navigate(`/workspace/setup/templates/template/${result.id}`, { replace: true })
  }

  const handleTemplateCreated = tracked(
    _handleTemplateCreated,
    { eventName: TrackEvent.NEW_TEMPLATE_CREATED },
    {
      eventDetailsExtractor: (result) => ({ ...getTemplateLibraryTrackingDetails(result), method: TemplateCreationMethod.Manual }),
    },
  )

  const handleSend = async (overrideMessage?: string, overrideSample?: string) => {
    const message = (overrideMessage ?? chatMessage).trim()
    const sampleToSend = overrideSample ?? addedSample
    if (!message || isLoading) return

    setIsLoading(true)
    try {
      dispatch(addMessage({ type: 'user', message }))

      const result = await createTemplate({
        accountId,
        request: {
          type: templateType,
          typeId: templateTypeId,
          scope: templateScope ?? 'user',
          name: templateName ?? 'New Template',
          description: templateDescription,
          template: { template: '' },
        },
      }).unwrap()
      await handleGeneratedTemplateCreated(result, message, sampleToSend)
    } catch (e) {
      console.error(e)
    } finally {
      setIsLoading(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      void handleSend()
    }
  }

  const handleAddSample = (sample: string) => {
    const messageToSend = chatMessage.trim() || 'Generate a template based on the provided example'
    setAddedSample(sample)
    setShowAddSampleModal(false)
    setChatMessage(messageToSend)
    void handleSend(messageToSend, sample)
  }

  const handleSkip = async () => {
    setIsLoading(true)
    try {
      const result = await createTemplate({
        accountId,
        request: {
          type: templateType,
          typeId: templateTypeId,
          scope: templateScope ?? 'user',
          name: templateName ?? 'New Template',
          description: templateDescription,
          template: { template: '' },
        },
      }).unwrap()
      handleTemplateCreated(result)
    } catch (e) {
      console.error(e)
    } finally {
      setIsLoading(false)
    }
  }

  if (!hasRequiredState) {
    return null
  }

  return (
    <>
      {portalRef && portalRef.current
        ? createPortal(
            <div className='d-flex align-items-center zocks-pl-2.0'>
              <Button variant='link' className='back-button' onClick={() => navigate(-1)}>
                <i className='bi bi-chevron-left' />
              </Button>
            </div>,
            portalRef.current,
          )
        : null}

      <AddSampleModal
        show={showAddSampleModal}
        onCancel={() => setShowAddSampleModal(false)}
        onAddSample={handleAddSample}
        initialValue={addedSample || undefined}
      />

      <div className='d-flex flex-column align-items-center overflow-y-auto w-100 px-5'>
        <div className='d-flex flex-column gap-4 pt-2 mw-100 w-75 px-5'>
          <div className='text-center px-5'>
            <h5 className='bold mb-0'>Create new {templateTypeName} template</h5>
            <p className='paragraph-medium zocks-text-gray-900 mb-0 mt-2'>Describe what you need, add a sample to base it on, or both.</p>
          </div>
          <div className='d-flex flex-column gap-3'>
            <div className='px-3'>
              <TemplateChatInputBar
                className='shadow w-100'
                value={chatMessage}
                onChange={setChatMessage}
                onSend={() => handleSend()}
                onKeyDown={handleKeyDown}
                addedSample={addedSample}
                onAddSampleClick={() => setShowAddSampleModal(true)}
                onRemoveSample={() => {
                  setAddedSample('')
                  if (chatMessage === 'Generate a template based on the provided example') {
                    setChatMessage('')
                  }
                }}
                isLoading={isLoading}
                placeholder='Describe the template you want...'
                sendIconDirection='right'
              />
            </div>
            <Button variant='primaryLinkButton' onClick={handleSkip} disabled={isLoading} className='button-medium text-center w-100'>
              Skip and create manually
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}
